// schema.org JSON-LD builders. Pure functions returning plain objects (no JSX,
// no text children → no `i18next/no-literal-string` violations). Rendered as
// <script type="application/ld+json"> by MetadataRenderer / <Seo>.
//
// `origin` is passed in (SITE_URL on the client, CANONICAL_ORIGIN at build/edge)
// so these stay runtime-agnostic.

import type { BlogPost } from '../components/blog/data';
import { APP_STORE_URL, PLAY_STORE_URL } from '../lib/store-links';
import { LOGO_IMAGE, LOGO_IMAGE_SIZE, toIsoDate } from '../seo/marketing-routes';

/** The site's one home URL — the landing's canonical (`/` redirects to it). */
const homeUrl = (origin: string) => `${origin}/home`;

const logo = (origin: string) => ({
  '@type': 'ImageObject',
  url: `${origin}${LOGO_IMAGE}`,
  width: LOGO_IMAGE_SIZE,
  height: LOGO_IMAGE_SIZE,
});

const SOCIAL_LINKS = [
  'https://www.instagram.com/flashka_study',
  'https://www.tiktok.com/@flashka_ai',
  'https://www.linkedin.com/company/flashka-ai',
];

/** Site-wide publisher identity. Mounted once (not per page). */
export const buildOrganization = (origin: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${origin}/#organization`,
  name: 'Flashka',
  url: homeUrl(origin),
  logo: logo(origin),
  sameAs: SOCIAL_LINKS,
});

/** Site-wide WebSite node. SearchAction omitted (no public search-results URL). */
export const buildWebSite = (origin: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${origin}/#website`,
  name: 'Flashka',
  url: homeUrl(origin),
  publisher: { '@id': `${origin}/#organization` },
});

/**
 * The Flashka product itself, named plainly as the entity it is ("Flashka",
 * never a page's marketing title) so AI answers and search can quote it.
 *
 * Offers: the Free plan (price 0) — true on every platform. No
 * aggregateRating: the "2M+ students" figure is marketing copy, and our store
 * ratings are collected on the App Store / Google Play; Google's review-snippet
 * rules forbid marking up ratings aggregated from other sites (a manual-action
 * risk).
 */
export const buildSoftwareApplication = (
  origin: string,
  opts: { description: string; inLanguage?: string; featureList?: string[] },
) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  '@id': `${origin}/#app`,
  name: 'Flashka',
  description: opts.description,
  applicationCategory: 'EducationalApplication',
  operatingSystem: 'Web, iOS, Android',
  url: homeUrl(origin),
  image: `${origin}/social-thumbnail.png`,
  ...(opts.inLanguage ? { inLanguage: opts.inLanguage } : {}),
  ...(opts.featureList?.length ? { featureList: opts.featureList } : {}),
  offers: {
    '@type': 'Offer',
    name: 'Free',
    price: '0',
    priceCurrency: 'USD',
  },
  sameAs: [APP_STORE_URL, PLAY_STORE_URL],
  publisher: { '@id': `${origin}/#organization` },
});

/**
 * The two store apps, one node each: the same product as the web app
 * (`isRelatedTo` the `#app` node), installable from its own store. Free to
 * install; no rating, for the same reason as above.
 */
export const buildMobileApplications = (
  origin: string,
  opts: { description: string; inLanguage?: string },
) =>
  (
    [
      ['ios', 'iOS', APP_STORE_URL],
      ['android', 'Android', PLAY_STORE_URL],
    ] as const
  ).map(([id, operatingSystem, storeUrl]) => ({
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    '@id': `${origin}/#app-${id}`,
    name: 'Flashka',
    description: opts.description,
    applicationCategory: 'EducationalApplication',
    operatingSystem,
    url: storeUrl,
    installUrl: storeUrl,
    image: `${origin}${LOGO_IMAGE}`,
    ...(opts.inLanguage ? { inLanguage: opts.inLanguage } : {}),
    offers: { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD' },
    isRelatedTo: { '@id': `${origin}/#app` },
    publisher: { '@id': `${origin}/#organization` },
  }));

export interface BreadcrumbItem {
  label: string;
  href: string;
}

export const buildBreadcrumbs = (origin: string, items: BreadcrumbItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.label,
    item: `${origin}${item.href}`,
  })),
});

export interface FaqEntry {
  question: string;
  answer: string;
}

export const buildFaqPage = (entries: FaqEntry[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: entries.map((entry) => ({
    '@type': 'Question',
    name: entry.question,
    acceptedAnswer: { '@type': 'Answer', text: entry.answer },
  })),
});

export const buildArticle = (
  origin: string,
  post: BlogPost,
  opts: { lang: string } = { lang: 'en' },
) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.description,
  inLanguage: opts.lang,
  author: {
    '@type': 'Person',
    name: post.author.name,
    description: post.author.description,
  },
  datePublished: toIsoDate(post.date) ?? post.date,
  dateModified: toIsoDate(post.date) ?? post.date,
  image: `${origin}${post.image}`,
  keywords: post.hashtags.join(', '),
  publisher: {
    '@type': 'Organization',
    name: 'Flashka',
    logo: logo(origin),
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${origin}/blog/${post.id}`,
  },
});
